import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Shield, Key, X, Ban, CheckCircle2, Settings2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

// Friendly labels for each permission key returned by
// /api/auth/permissions/available. Keep in sync with backend
// ADMIN_PERMISSIONS. Section keys that don't have a label here still
// render — just with the raw key title-cased.
const PERM_LABELS = {
  nominations: 'Nominations',
  nominators: 'Registrations',
  payments: 'Payments · Mark Paid',
  scoring: 'Jury Scoring',
  categories: 'Categories',
  jurors: 'Jurors',
  sponsors: 'Sponsors',
  subscribers: 'Subscribers',
  contacts: 'Contacts',
  cms: 'CMS · Announcements',
  winners: 'Winners Manager',
  settings: 'Site Settings',
  admins: 'Admin Management (super-admin only)',
};

// Multi-admin management. Visible only to the seeded super_admin
// (awards@marketingmind.co) — every request is guarded by the
// `get_current_super_admin` dependency on the backend.
const AdminUsersTab = () => {
  const [admins, setAdmins] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [permsTarget, setPermsTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: perms }] = await Promise.all([
        ax.get(`${API}/admin/admins`),
        ax.get(`${API}/auth/permissions/available`),
      ]);
      setAdmins(list);
      setAvailable(perms.permissions || []);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error(e.response?.data?.detail || 'Failed to load admins.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (row) => {
    if (row.role === 'super_admin') { toast.error('Primary administrator cannot be deactivated.'); return; }
    if (!window.confirm(`${row.is_active ? 'Deactivate' : 'Reactivate'} ${row.email}?`)) return;
    try {
      await ax.patch(`${API}/admin/admins/${row.id}`, { is_active: !row.is_active });
      toast.success(`${row.email} ${row.is_active ? 'deactivated' : 'reactivated'}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  const remove = async (row) => {
    if (row.role === 'super_admin') return;
    if (!window.confirm(`Permanently revoke admin access for ${row.email}? Their login will stop working immediately.`)) return;
    try {
      await ax.delete(`${API}/admin/admins/${row.id}`);
      toast.success(`${row.email} removed.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  if (forbidden) {
    return (
      <div data-testid="admin-users-tab-forbidden" className="max-w-lg border border-[#C2613A]/40 bg-[#C2613A]/5 p-6">
        <div className="flex items-start gap-3">
          <Shield size={22} className="text-[#C2613A] mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-serif text-lg text-[var(--ink)]">Restricted section</h3>
            <p className="text-[13px] text-[var(--ink-muted)] mt-2 leading-relaxed">
              Only the primary administrator can manage other admin accounts.
              Please contact <em>awards@marketingmind.co</em> if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div>;

  return (
    <div data-testid="admin-users-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[var(--ink-muted)] text-sm max-w-2xl">
          Create sub-admin accounts with fine-grained access. Tick only the sections each sub-admin should see;
          any section you leave unchecked is hidden from their dashboard and blocked on the backend.
          The primary administrator always has full access.
        </p>
        <button onClick={() => setShowCreate(true)} data-testid="admin-users-create-btn"
                className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-2.5 text-[10.5px] tracking-[0.28em] uppercase font-semibold">
          <UserPlus size={14} /> Create Admin
        </button>
      </div>

      <div className="overflow-x-auto border border-[var(--gold-faint)]">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-[var(--bg-alt)]">
            <tr>{['Email', 'Name', 'Role', 'Permissions', 'Status', 'Last Login', 'Actions'].map((h) => (
              <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {admins.map((a, i) => (
              <tr key={a.id} className="border-t border-[var(--gold-faint)]/40 align-top" data-testid={`admin-row-${i}`}>
                <td className="px-4 py-3 text-[var(--ink)]">{a.email}</td>
                <td className="px-4 py-3 text-[var(--ink)]">{a.name}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${a.role === 'super_admin' ? 'bg-[var(--brand-brass)] text-white border-[var(--brand-brass)]' : 'text-[var(--gold)] border-[var(--gold-faint)]'}`}>
                    {a.role === 'super_admin' ? 'Primary' : 'Admin'}
                  </span>
                </td>
                <td className="px-4 py-3 max-w-[280px]">
                  {a.role === 'super_admin' ? (
                    <span className="text-[11px] text-[var(--ink-muted)] italic">All sections</span>
                  ) : (
                    <div className="flex flex-wrap gap-1" data-testid={`admin-perms-${i}`}>
                      {(a.permissions || []).length === 0 && <span className="text-[11px] text-[#C2613A] italic">No access</span>}
                      {(a.permissions || []).map((p) => (
                        <span key={p} className="text-[9.5px] tracking-[0.12em] uppercase px-1.5 py-0.5 bg-[var(--gold-faint)]/40 text-[var(--ink)] border border-[var(--gold-faint)]">
                          {(PERM_LABELS[p] || p).split(' · ')[0]}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] tracking-[0.2em] uppercase ${a.is_active ? 'text-[#5C9F60]' : 'text-[#C2613A]'}`}>
                    {a.is_active ? 'Active' : 'Deactivated'}
                  </span>
                </td>
                <td className="px-4 py-3 text-[var(--ink-muted)] text-xs">{a.last_login ? new Date(a.last_login).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">
                  {a.role !== 'super_admin' && (
                    <button onClick={() => setPermsTarget(a)} data-testid={`admin-perms-edit-${i}`}
                            className="inline-flex items-center gap-1 text-[var(--walnut)] hover:text-[var(--brand-brass)] mr-3 font-semibold">
                      <Settings2 size={11} /> Permissions
                    </button>
                  )}
                  <button onClick={() => setResetTarget(a)} data-testid={`admin-reset-${i}`}
                          className="inline-flex items-center gap-1 text-[var(--gold)] hover:text-[var(--brand-brass)] mr-3">
                    <Key size={11} /> Reset Password
                  </button>
                  {a.role !== 'super_admin' && (
                    <>
                      <button onClick={() => toggleActive(a)} data-testid={`admin-toggle-${i}`}
                              className="inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)] mr-3">
                        {a.is_active ? <><Ban size={11} /> Deactivate</> : <><CheckCircle2 size={11} /> Activate</>}
                      </button>
                      <button onClick={() => remove(a)} data-testid={`admin-remove-${i}`}
                              className="inline-flex items-center gap-1 text-[#C2613A] hover:opacity-80">
                        Remove
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {admins.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--ink-muted)]">No other admins yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateAdminModal available={available} onClose={() => setShowCreate(false)} onCreated={load} />}
      {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} />}
      {permsTarget && <EditPermissionsModal available={available} target={permsTarget} onClose={() => setPermsTarget(null)} onSaved={load} />}
    </div>
  );
};

// Reusable permission-picker used by both create and edit modals. Groups
// permission keys visually and hides the super-admin-only `admins` key.
const PermissionsPicker = ({ available, selected, onChange }) => {
  const options = available.filter((k) => k !== 'admins');
  const toggle = (k) => {
    if (selected.includes(k)) onChange(selected.filter((x) => x !== k));
    else onChange([...selected, k]);
  };
  const setAll = () => onChange(options);
  const setNone = () => onChange([]);
  return (
    <div className="border border-[var(--gold-faint)] p-4 bg-[var(--bg-alt)]/40">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Sections this admin can access</div>
        <div className="text-[10.5px] flex gap-3">
          <button type="button" onClick={setAll} data-testid="perm-select-all" className="text-[var(--walnut)] hover:underline">Select all</button>
          <button type="button" onClick={setNone} data-testid="perm-select-none" className="text-[var(--ink-muted)] hover:underline">Clear</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((k) => {
          const checked = selected.includes(k);
          return (
            <label key={k} className="flex items-start gap-2 cursor-pointer p-2 hover:bg-white/60 transition-colors" data-testid={`perm-option-${k}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(k)} className="mt-0.5 accent-[var(--walnut)]" />
              <span className="text-[13px] text-[var(--ink)] leading-tight">{PERM_LABELS[k] || k}</span>
            </label>
          );
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-[11px] text-[#C2613A] italic mt-3">
          ⚠ With no sections selected the admin will see an empty dashboard. Tick at least one.
        </p>
      )}
    </div>
  );
};

const CreateAdminModal = ({ available, onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  // Sensible default — everything except admin management.
  const [permissions, setPermissions] = useState(available.filter((k) => k !== 'admins'));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await ax.post(`${API}/admin/admins`, { email, name, password, permissions });
      toast.success(`Admin ${email} created with ${permissions.length} section${permissions.length === 1 ? '' : 's'} of access.`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#1a0e08]/90 backdrop-blur-md flex items-start justify-center overflow-y-auto" data-testid="admin-create-modal">
      <div className="bg-white border border-[var(--gold-soft)] max-w-2xl w-full my-8 mx-4 p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(58,36,24,0.55)]">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">New Admin</div>
            <h3 className="font-serif text-xl text-[var(--ink)] mt-1">Create Admin Account</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--walnut)]" data-testid="admin-create-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">Email *</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="admin-create-email"
              className="w-full border border-[var(--gold-faint)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">Full Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-create-name"
              className="w-full border border-[var(--gold-faint)] px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">Password (min 8 chars) *</label>
            <input value={password} onChange={(e) => setPassword(e.target.value)} type="text" data-testid="admin-create-password"
              className="w-full border border-[var(--gold-faint)] px-3 py-2 text-sm font-mono" />
            <p className="text-[10.5px] text-[var(--ink-muted)] italic mt-1">Share this via a secure channel — they can log in at /admin/login.</p>
          </div>
        </div>

        <PermissionsPicker available={available} selected={permissions} onChange={setPermissions} />

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} disabled={saving} className="flex-1 border border-[var(--gold-soft)] py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving || !email || !name || !password} data-testid="admin-create-confirm"
                  className="flex-1 bg-[var(--walnut)] text-white py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Admin'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditPermissionsModal = ({ target, available, onClose, onSaved }) => {
  const [permissions, setPermissions] = useState(target.permissions || []);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await ax.patch(`${API}/admin/admins/${target.id}`, { permissions });
      toast.success(`Permissions updated for ${target.email}.`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#1a0e08]/90 backdrop-blur-md flex items-start justify-center overflow-y-auto" data-testid="admin-perms-modal">
      <div className="bg-white border border-[var(--gold-soft)] max-w-2xl w-full my-8 mx-4 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Permissions</div>
            <h3 className="font-serif text-xl text-[var(--ink)] mt-1">{target.email}</h3>
            <p className="text-[12px] text-[var(--ink-muted)] mt-1">Choose exactly which sections this admin can view and modify.</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--walnut)]"><X size={18} /></button>
        </div>
        <PermissionsPicker available={available} selected={permissions} onChange={setPermissions} />
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} disabled={saving} className="flex-1 border border-[var(--gold-soft)] py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="admin-perms-save"
                  className="flex-1 bg-[var(--walnut)] text-white py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ target, onClose }) => {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await ax.post(`${API}/admin/admins/${target.id}/reset-password`, { password });
      toast.success(`Password reset for ${target.email}.`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#1a0e08]/90 backdrop-blur-md flex items-center justify-center px-4 py-8" data-testid="admin-reset-modal">
      <div className="bg-white border border-[var(--gold-soft)] max-w-md w-full p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Reset Password</div>
            <h3 className="font-serif text-xl text-[var(--ink)] mt-1">{target.email}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)]"><X size={18} /></button>
        </div>
        <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">New Password (min 8 chars) *</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="text" data-testid="admin-reset-password-input"
          className="w-full border border-[var(--gold-faint)] px-3 py-2 text-sm mb-6 font-mono" />
        <p className="text-[10.5px] text-[var(--ink-muted)] italic mb-4">
          The user&rsquo;s old password will stop working immediately. Share the new one securely.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={saving} className="flex-1 border border-[var(--gold-soft)] py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving || !password} data-testid="admin-reset-confirm"
                  className="flex-1 bg-[var(--walnut)] text-white py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            {saving ? 'Saving…' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersTab;
